import { useState, useEffect, useLayoutEffect } from "react";

// react-router
import { useLocation } from "react-router-dom";

// ui
import { Backdrop } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

// jotai
import { useAtom, useSetAtom, useAtomValue } from "jotai";
import { AppAtoms } from "./lib/store";
import { useWindowSize } from "./lib/useWindowSize";
import { CONST } from "./config/const";

// page
import EntryForm from "./page/EntryForm";
import CancellForm from "./page/CancellForm";

// api
import axios from "axios";
import { getSiteLayout, getCancellSiteLayout } from "./api/siteLayout";

function App() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [isLoading, setIsLoading] = useAtom(AppAtoms.isLoading);
  const [layoutConfig, setLayoutConfig] = useAtom(AppAtoms.layoutConfig);
  const setPdfDatas = useSetAtom(AppAtoms.pdfDatas);
  const setPaymentMethod = useSetAtom(AppAtoms.paymentMethod);
  const setPullDownService = useSetAtom(AppAtoms.pullDownService);
  const setCheckBoxService = useSetAtom(AppAtoms.checkBoxService);
  const setOptions = useSetAtom(AppAtoms.options);

  const setIsSmartPhone = useSetAtom(AppAtoms.isSmartPhone);
  const setApiKey = useSetAtom(AppAtoms.apiKey);
  const setInputID = useSetAtom(AppAtoms.inputID);
  const setDisplay = useSetAtom(AppAtoms.display);
  const [pathname, setPathname] = useAtom(AppAtoms.pathname);
  const setBeforeInputCustomerInfo = useSetAtom(
    AppAtoms.beforeInputCustomerInfo
  );
  const { windowWidth, windowHeight } = useWindowSize();

  const [layoutStatus, setLayoutStatus] = useAtom(AppAtoms.layoutStatus);

  const getCurrentDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // 月を2桁にフォーマット
    const day = String(today.getDate()).padStart(2, "0"); // 日を2桁にフォーマット

    return `${year}年${month}月${day}日`;
  };
  useLayoutEffect(() => {
    const isSmartPhone =
      windowWidth < 400 ||
      !!navigator.userAgent.match(/iPhone|Android.+Mobile/);
    setIsSmartPhone(isSmartPhone);
  }, [windowWidth]);

  useEffect(() => {
    // 外部ETフォーム取得
    async function getSiteLayoutInf(
      inputID: string,
      display: string,
      pathName: string,
      paymentType: string | null
    ) {
      const {
        layout_data,
        header_location_favicon,
        header_location_logo,
        pdf_datas,
        trial_period_months,
        service_option_name_pull_down,
        service_option_name_check_box,
        service_required_optional_pull_down,
        service_required_optional_check_box,
        payment_method,
        pull_down_service,
        check_box_service,
        options,
        input_customer_info,
      } = await getSiteLayout(inputID, display, pathName);

      // faviconの設定
      let link = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = header_location_favicon;

      // タイトルの設定
      document.title = layout_data.site_title;

      setLayoutConfig({
        ...layout_data,
        trial_period_months,
        service_option_name_pull_down,
        service_option_name_check_box,
        service_required_optional_pull_down,
        service_required_optional_check_box,
        header_location_logo,
        outside_cancell_form_text: "",
        outside_cancell_form_mail_address: "",
      });
      setPdfDatas(pdf_datas);
      setPaymentMethod(payment_method);
      setPullDownService(pull_down_service);
      setCheckBoxService(check_box_service);
      setOptions(options);
      setBeforeInputCustomerInfo(input_customer_info);
      if (paymentType == "j-payment" || paymentType == "sb-payment") {
        setLayoutStatus("entry_success");
      } else {
        setLayoutStatus("entry_input");
      }
      setInputID(inputID);
      setDisplay(display);
      setIsLoading(false);
    }

    // 解約用レイアウト取得
    async function getCancellSiteLayoutInf(display: string, pathName: string) {
      const {
        layout_data,
        header_location_favicon,
        header_location_logo,
        pdf_datas,
      } = await getCancellSiteLayout(display, pathName);

      // faviconの設定
      let link = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = header_location_favicon;

      // タイトルの設定
      document.title = layout_data.site_title;

      setLayoutConfig({
        ...layout_data,
        trial_period_months: "",
        service_option_name_pull_down: "",
        service_option_name_check_box: "",
        service_required_optional_pull_down: "",
        service_required_optional_check_box: "",
        header_location_logo,
      });
      setPdfDatas(pdf_datas);
      setLayoutStatus("cancell_input");
      setDisplay(display);
      setIsLoading(false);
    }

    // パスの取得
    let pathName = location.pathname;
    if (pathName === "" || pathName === "/") {
      const _pathName = query.get("pathname");
      pathName = _pathName ? _pathName : "";
      setPathname(pathName);
    } else {
      setPathname(pathName);
    }
    if (!pathName.endsWith("/cancellation")) {
      // 入力フォーム
      const key = query.get("key");
      axios.defaults.headers.common["X-API-KEY"] = key;
      const paymentType = query.get("paymentType");
      if (key) {
        let inputID = query.get("inputID");
        let display = query.get("display");
        getSiteLayoutInf(
          inputID ? inputID : "",
          display ? display : "",
          pathName,
          paymentType
        );
        setApiKey(key);
      } else {
        // keyなし　→　レイアウト非表示
        setLayoutStatus("none_key");
        setIsLoading(false);
      }
    } else {
      // 退会フォーム
      const key = CONST.cancellApiKey;
      axios.defaults.headers.common["X-API-KEY"] = key;
      let display = query.get("display");
      getCancellSiteLayoutInf(display ? display : "", pathName);
      setApiKey(key);
    }
  }, []);
  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {layoutStatus == "loading" ? null : layoutStatus == "entry_success" ? (
        <div className="contact_wrap">
          <h2>お申し込みありがとうございます</h2>
          {layoutConfig.service_page_url !== "" ? (
            <div className="entry_success_box">
              <div>
                サービスページは
                <a href={layoutConfig.service_page_url}>こちら</a>
              </div>
            </div>
          ) : null}
        </div>
      ) : layoutStatus == "entry_error" ? (
        <div className="contact_wrap">
          <div className="entry_success_box">
            <div>登録中にエラーが発生しました。</div>
            {layoutConfig.service_page_url !== "" ? (
              <div>
                <a href={layoutConfig.service_page_url}>サービスページ</a>
                よりお問い合わせください。
              </div>
            ) : null}
          </div>
        </div>
      ) : layoutStatus == "cancell_error" ? (
        <div className="contact_wrap">
          <div className="entry_success_box">
            <div>解約中にエラーが発生しました。</div>
            {layoutConfig.service_page_url !== "" ? (
              <div>
                <a href={layoutConfig.service_page_url}>サービスページ</a>
                よりお問い合わせください。
              </div>
            ) : null}
          </div>
        </div>
      ) : layoutStatus == "entry_duplicate" ? (
        <div className="contact_wrap">
          <div className="entry_success_box">
            <div>既に登録済の申込です。</div>
            {layoutConfig.service_page_url !== "" ? (
              <div>
                <a href={layoutConfig.service_page_url}>サービスページ</a>
                よりお問い合わせください。
              </div>
            ) : null}
          </div>
        </div>
      ) : layoutStatus == "entry_input" ? (
        <EntryForm />
      ) : layoutStatus == "cancell_input" ? (
        <CancellForm />
      ) : layoutStatus == "cancell_success" ? (
        <div className="contact_wrap">
          <h2>ご解約を受け付けました</h2>
          <div className="entry_success_box">
            <div>解約受付日時：{getCurrentDate()}</div>
            <div style={{ marginTop: "16px" }}>
              ※ご解約処理の証として、こちらの画面をスクリーンショットなどで保管いただきますようお願いいたします。
            </div>
            <div style={{ marginTop: "16px" }}>
              3営業日以内に解約完了のメールを送信いたします。
            </div>
            {layoutConfig.outside_cancell_form_mail_address !== "" ? (
              <div>
                迷惑メール設定をされているお客様は
                <br />
                {layoutConfig.outside_cancell_form_mail_address}
                <br />
                を迷惑メール設定から外して頂けますようお願いいたします。
              </div>
            ) : null}
            <div style={{ marginTop: "16px" }}>迷惑メール設定解除方法</div>
            <div>
              <a
                href="https://www.docomo.ne.jp/faq/detail?faqId=77567"
                target="_blank"
                rel="noopener noreferrer"
              >
                docomo
              </a>
            </div>
            <div>
              <a
                href="https://www.au.com/support/service/internet/trouble/mail/mail-option-03/#anc03"
                target="_blank"
                rel="noopener noreferrer"
              >
                au
              </a>
            </div>
            <div>
              <a
                href="https://www.softbank.jp/support/faq/view/12592"
                target="_blank"
                rel="noopener noreferrer"
              >
                Softbank
              </a>
            </div>
            <div>
              <a
                href="https://support.google.com/mail/answer/8151?sjid=6499765189702637227-NC"
                target="_blank"
                rel="noopener noreferrer"
              >
                Gmail
              </a>
            </div>
            {layoutConfig.service_page_url !== "" ? (
              <div style={{ marginTop: "16px" }}>
                サービスページは
                <a href={layoutConfig.service_page_url}>こちら</a>
              </div>
            ) : null}
          </div>
        </div>
      ) : layoutStatus == "none_key" ? (
        <div className="contact_wrap">
          <div className="entry_success_box">
            <div>サイトキーがURLに含まれていません。</div>
            <div>担当者にご確認ください。</div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default App;
